import { getDateObjectFromDetails, getRemainingTimeInHoursAndMinutes } from './commonLtsUtils';
import { CURRENT_STATION_DETAILS_CARD, DAY_NAMES } from '../constants/LtsConstants';

const KEY_FOR_UNKNOWN_DETAILS_COLOR = 'noLiveDetailsKnown';
const KEY_FOR_CANCELLED_DETAILS_COLOR = 'cancelledStation';
const KEY_FOR_DIVERTED_DETAILS_COLOR = 'divertedStation';
const KEY_FOR_CROSSED_STOPPING_DETAILS_COLOR = 'crossedAndStoppingStation';

const KEY_FOR_UNKNOWN_DETAILS_DOT = 'noLiveDetailsDot';
const KEY_FOR_CANCELLED_DETAILS_DOT = 'cancelledStationDot';
const KEY_FOR_DIVERTED_DETAILS_DOT = 'divertedStationDot';
const KEY_FOR_CROSSED_STOPPING_DOT = 'crossedAndStoppingStationDot';

const stationColorToDotColorMap = {
  [KEY_FOR_UNKNOWN_DETAILS_COLOR]: KEY_FOR_UNKNOWN_DETAILS_DOT,
  [KEY_FOR_CANCELLED_DETAILS_COLOR]: KEY_FOR_CANCELLED_DETAILS_DOT,
  [KEY_FOR_DIVERTED_DETAILS_COLOR]: KEY_FOR_DIVERTED_DETAILS_DOT,
  [KEY_FOR_CROSSED_STOPPING_DETAILS_COLOR]: KEY_FOR_CROSSED_STOPPING_DOT
};

export function getCurrentStationCardDetails({ currentStation, currentIntermediateStation }) {
  const { CROSSED_STATION, ARRIVED_AT_STATION } = CURRENT_STATION_DETAILS_CARD;
  if (currentIntermediateStation) {
    const { stationName } = currentIntermediateStation;
    return {
      stationName,
      isHalting: false,
      actualTime: undefined,
      crossedStationText: CROSSED_STATION,
      blackWatch: true
    };
  }

  const {
    stationName,
    actualDepartureDetails: { actualDepartureTime },
    actualArrivalDetails: { actualArrivalTime }
  } = currentStation;

  function getActualTimeForCurrentHaltingStation() {
    const { hasDeparted } = currentStation;
    return hasDeparted ? actualDepartureTime : actualArrivalTime;
  }

  function getCrossedStationTextForCurrentHaltingStation() {
    const { hasDeparted } = currentStation;
    return hasDeparted ? CROSSED_STATION : ARRIVED_AT_STATION;
  }
  function isWatchBlack() {
    const { hasDeparted } = currentStation;
    return hasDeparted ? currentStation.departedStationOnTime : currentStation.arrivedStationOnTime;
  }
  return {
    stationName,
    isHalting: true,
    actualTime: getActualTimeForCurrentHaltingStation(),
    crossedStationText: getCrossedStationTextForCurrentHaltingStation(),
    blackWatch: isWatchBlack()
  };
}
/*
TODO: use the following function for displaying train between two stations card for train schedule api
*/
export function getLtsCardDetailsBetweenOriginAndDestinationStop({ originStation, destinationStation }) {
  const originStationName = originStation.stationCode;
  const destinationStationName = destinationStation.stationCode;
  const {
    scheduledDepartureDetails: {
      scheduledDepartureTime: departureTimeOfOriginStation,
      scheduledDepartureDate: originStopScheduledDepartureDate
    }
  } = originStation;
  const {
    scheduledArrivalDetails: {
      scheduledArrivalTime: arrivalTimeOfDestinationStation,
      scheduledArrivalDate: destinationStopScheduledArrivalDate
    }
  } = destinationStation;
  const NUMBER_OF_MILLI_SECONDS_PER_DAY = 86400000;
  const dayDifference =
    (getDateObjectFromDetails({ dateInString: destinationStopScheduledArrivalDate, timeInString: '00:00' }) -
      getDateObjectFromDetails({ dateInString: originStopScheduledDepartureDate, timeInString: '00:00' })) /
    NUMBER_OF_MILLI_SECONDS_PER_DAY;
  return {
    originStationName,
    destinationStationName,
    departureTimeOfOriginStation,
    arrivalTimeOfDestinationStation,
    dayDifference
  };
}

export function getLtsCardDetailsBetweenFirstAndLastStop({
  firstStoppingStation,
  lastStoppingStation,
  totalDistance,
  currentStation,
  currentIntermediateStation
}) {
  const firstStationName = firstStoppingStation.stationCode;
  const lastStationName = lastStoppingStation.stationCode;
  const {
    scheduledDepartureDetails: {
      scheduledDepartureTime: firstStopScheduledDepartureTime,
      scheduledDepartureDate: firstStopScheduledDepartureDate
    }
  } = firstStoppingStation;
  const {
    scheduledArrivalDetails: {
      scheduledArrivalTime: lastStopScheduledArrivalTime,
      scheduledArrivalDate: lastStopScheduledArrivalDate
    }
  } = lastStoppingStation;
  const currentMostStation = currentIntermediateStation || currentStation;
  const distanceTravelled = Number(currentMostStation.distanceFromOriginStation);
  const remainingDistance = `${(Number(totalDistance) - Number(currentMostStation.distanceFromOriginStation)).toFixed(
    2
  )} kms`;

  const NUMBER_OF_MILLI_SECONDS_PER_DAY = 86400000;
  const currentDateObject = new Date();
  const remainingTime = getRemainingTimeInHoursAndMinutes(
    getDateObjectFromDetails({
      dateInString: lastStopScheduledArrivalDate,
      timeInString: lastStopScheduledArrivalTime
    }),
    currentDateObject
  );
  const dayDifference =
    (getDateObjectFromDetails({ dateInString: lastStopScheduledArrivalDate, timeInString: '00:00' }) -
      getDateObjectFromDetails({ dateInString: firstStopScheduledDepartureDate, timeInString: '00:00' })) /
    NUMBER_OF_MILLI_SECONDS_PER_DAY;
  const isOnTime = currentStation.arrivedStationOnTime;

  return {
    departureTimeOfFirstStop: firstStopScheduledDepartureTime,
    arrivalTimeOfLastStop: lastStopScheduledArrivalTime,
    dayDifference,
    isOnTime,
    remainingDistance,
    remainingTime,
    firstStationName,
    lastStationName,
    distanceTravelled
  };
}

export function getFontStyleForActual({ scheduledDate, scheduledTime, actualDate, actualTime }) {
  if (!actualDate || !actualTime) {
    return 'blackText latoBold';
  } else if (scheduledDate === actualDate && scheduledTime === actualTime) {
    return 'greenText latoBold';
  } else {
    return 'redText latoBold';
  }
}

export function formatDateForLtsDatePicker(dateObject) {
  const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
  return `${dateObject.getDate()} ${MONTH_NAMES[dateObject.getMonth()]} ${dateObject.getFullYear()}`;
}

export function getCurrentWeekDates(daysOfRun) {
  const week = DAY_NAMES.map((dayName, index) => {
    const curr = new Date();
    const first = curr.getDate() - curr.getDay() + index;
    const date = new Date(curr.setDate(first));
    const weekDay = dayName;
    const formattedString = formatDateForLtsDatePicker(date);
    return {
      date,
      weekDay,
      formattedString,
      isActive: daysOfRun.charAt(index) === '1'
    };
  });
  return week;
}

export function getColorForStationBeingPassed({
  haltingStation,
  haltingStationCollection,
  currentStation,
  trainStartedFromSource
}) {
  const haltingStationIndex = haltingStationCollection.findIndex((station) => station === haltingStation);
  const currentStationIndex = haltingStationCollection.findIndex((station) => station === currentStation);

  function getStationColorForStoppingStation() {
    if (!trainStartedFromSource) {
      return KEY_FOR_UNKNOWN_DETAILS_COLOR;
    }
    if (haltingStationIndex <= currentStationIndex) {
      return KEY_FOR_CROSSED_STOPPING_DETAILS_COLOR;
    }
    return KEY_FOR_UNKNOWN_DETAILS_COLOR;
  }

  const { stoppingStation, divertedStation, cancelledStation } = haltingStation;

  if (cancelledStation) {
    return KEY_FOR_CANCELLED_DETAILS_COLOR;
  }
  if (divertedStation) {
    return KEY_FOR_DIVERTED_DETAILS_COLOR;
  }
  if (stoppingStation) {
    return getStationColorForStoppingStation();
  }
  return KEY_FOR_UNKNOWN_DETAILS_COLOR;
}

export function getStationCrossedDot({
  haltingStation,
  haltingStationCollection,
  currentStation,
  trainStartedFromSource
}) {
  const colorForStation = getColorForStationBeingPassed({
    haltingStation,
    haltingStationCollection,
    currentStation,
    trainStartedFromSource
  });
  return stationColorToDotColorMap[colorForStation];
}

export function getStationCrossDotIntermediate({
  haltingStation,
  haltingStationCollection,
  currentStation,
  trainStartedFromSource,
  currentIntermediateStation,
  intermediateStationToColor,
  intermediateStations
}) {
  const haltingStationDot = getStationCrossedDot({
    haltingStation,
    haltingStationCollection,
    currentStation,
    trainStartedFromSource
  });
  const isCurrentStation = currentStation === haltingStation;
  if (!isCurrentStation) {
    return haltingStationDot;
  }
  if (!currentIntermediateStation) {
    return KEY_FOR_UNKNOWN_DETAILS_DOT;
  }
  const currentIntermediateStationIndex = intermediateStations.findIndex(currentIntermediateStation);
  const intermediateStationToColorIndex = intermediateStations.findIndex(intermediateStationToColor);
  if (intermediateStationToColorIndex < currentIntermediateStationIndex) {
    return haltingStationDot;
  } else {
    return KEY_FOR_UNKNOWN_DETAILS_DOT;
  }
}

export function getColorForIntermediateStationAndBelowItFromDotColor(stationDotColor) {
  return Object.keys(stationColorToDotColorMap).find(
    (stationColor) => stationColorToDotColorMap[stationColor] === stationDotColor
  );
}

function getRunningDaysInFormattedForm(trainRunningDays) {
  const orderToCheckRunningDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const actuallyRunningOn = orderToCheckRunningDays.filter((day) => trainRunningDays[day]);
  return actuallyRunningOn.join(', ');
}

function getNumberOfRunningDays(trainRunningDays) {
  const numberOfRunningDays = Object.values(trainRunningDays).filter((day) => day === true).length;
  return numberOfRunningDays;
}

function trainNameDoesNotHaveTrainNumber(trainName) {
  if (trainName.length < 5) return true;
  return isNaN(trainName.substring(0, 5));
}

export default function getTrainNameWithNumber(trainName, trainNumber) {
  if (!trainName) return;
  let trainNamewithNumber = trainName;
  if (trainNameDoesNotHaveTrainNumber(trainNamewithNumber)) {
    trainNamewithNumber = `${trainNumber} ${trainName}`;
  }
  return trainNamewithNumber;
}

function getTrainAvailableClassContent({ trainInfoRelatedTokens: { train_number, train_name, available_classes } }) {
  const sectionTitle = `Available Classes in ${getTrainNameWithNumber(train_name, train_number)}`;
  const paraContentCollection = [
    `You can travel in ${getTrainNameWithNumber(train_name, train_number)} across the major ticketing classes such as `,
    `<strong>${available_classes?.join(',')}</strong>. `,
    `When boarding your train, you can also check the platform your train will arrive at via the `,
    `<strong>${train_name} live status</strong> `,
    `portal of MakeMyTrip. `,
    `The platform number is available for all halts across the train route between source and destination stations.`,
    `</br></br>`,
    `The live <strong>${train_number} train running status</strong> `,
    `feature makes your journey between source and destination stations convenient, ensuring you reach `,
    `your boarding point in time and stay updated on all changes in your train's schedule. `,
    `With data sourced from NTES, the live train tracking platform of Indian Railways, MakeMyTrip enables accurate and quick display of  `,
    `<strong>${train_name} running status</strong>.`
  ];
  const key = 'TrainAvailableClassesContent';
  return {
    sectionTitle,
    paraContentCollection,
    key
  };
}

function getTrainRouteContent({ trainInfoRelatedTokens: { train_number, train_name } }) {
  const sectionTitle = `Train Route of ${getTrainNameWithNumber(train_name, train_number)}`;
  const paraContentCollection = [
    `Track <strong>${train_number} train running status</strong> `,
    `live across the train route between source and destination stations through MakeMyTrip. `,
    `While taking a look at the `,
    `<strong>${getTrainNameWithNumber(train_name, train_number)} train running status</strong> `,
    `on MakeMyTrip, `,
    `you can also check the other detailed information available about the route.`,
    `</br></br>`,
    `You can check the total distance covered from the source station, the total distance remaining `,
    `to be covered, the time remaining for journey completion, the number of stations before your `,
    `deboarding station, the halt duration for your deboarding station and more. With a quick checking of `,
    `<strong>${train_name} live status</strong>, `,
    `eliminate the hassles of last-minute boarding/deboarding and travel inconvenience.`
  ];
  const key = 'TrainRouteInfoContent';
  return {
    sectionTitle,
    paraContentCollection,
    key
  };
}

function getTrainStatusInfoContent({
  trainInfoRelatedTokens: {
    train_number,
    train_name,
    source_station_name,
    destination_station_name,
    train_total_distance
  }
}) {
  const sectionTitle = `${getTrainNameWithNumber(train_name, train_number)} Train Live Running Status on MakeMyTrip`;
  const paraContentCollection = [
    `Planning a train journey from ${source_station_name} to ${destination_station_name} anytime soon? To make your journeys more hassle-free, you can now check the `,
    `<strong>${getTrainNameWithNumber(train_name, train_number)} train live running status</strong>`,
    ` on MakeMyTrip. While travelling from `,
    `${source_station_name} to ${destination_station_name}, the total distance covered is ${train_total_distance} km.`
  ];
  const key = 'TrainStatusInfoContent';
  return {
    sectionTitle,
    paraContentCollection,
    key
  };
}

function getTrainScheduleInfoContent({
  trainInfoRelatedTokens: {
    train_number,
    train_name,
    source_station_name,
    destination_station_name,
    source_station_code,
    destination_station_code,
    train_running_days,
    number_of_stations_covered,
    scheduled_departure_time_from_source,
    scheduled_arrival_time_to_destination
  }
}) {
  const sectionTitle = `${getTrainNameWithNumber(train_name, train_number)} Schedule`;
  const paraContentCollection = [
    `Currently, ${getTrainNameWithNumber(train_name, train_number)} operates on ${getNumberOfRunningDays(
      train_running_days
    )} days a week on `,
    `${getRunningDaysInFormattedForm(train_running_days)}`,
    `. It operates from `,
    `${source_station_name} (${source_station_code}) to ${destination_station_name} (${destination_station_code}).`,
    ` As for the halts, there are around `,
    `${number_of_stations_covered}`,
    ` stations along the way. To monitor the live location of your train or check the upcoming station&rsquo;s name, you can simply rely on MakeMyTrip&rsquo;s `,
    `<a href="https://www.makemytrip.com/railways/liveStatus/"><strong>train running status</strong></a>`,
    ` option. ${getTrainNameWithNumber(train_name, train_number)}&rsquo;s departure time from its source station is `,
    `${scheduled_departure_time_from_source}`,
    `. And, it reaches its destination at around `,
    `${scheduled_arrival_time_to_destination}`,
    `.`
  ];
  const key = 'TrainScheduleInfoContent';
  return {
    sectionTitle,
    paraContentCollection,
    key
  };
}

function getOnlineBookingInfoContent({ trainInfoRelatedTokens: { train_number, train_name } }) {
  const sectionTitle = `Online Booking of ${getTrainNameWithNumber(train_name, train_number)} Train on MakeMyTrip`;
  const paraContentCollection = [
    `Apart from `,
    `<strong>${getTrainNameWithNumber(train_name, train_number)} train live running status</strong>`,
    `, MakeMyTrip also allows you to book tickets with complete ease. To book your online tickets, first, enter your source and destination. Now, pick the right timing as per your plans, and choose your preferred train class and berth type. Lastly, complete the payment process carefully and then use your `,
    `<a href="https://www.makemytrip.com/railways/irctc-login.html"><strong>IRCTC login ID</strong></a>`,
    ` to confirm your booking. And that&rsquo;s it; you have done all you need to do for your `,
    `<a href="https://www.makemytrip.com/railways/"><strong>IRCTC ticket booking</strong></a>`,
    ` on MakeMyTrip.`
  ];
  const key = 'OnlineBookingInfoContent';
  return {
    sectionTitle,
    paraContentCollection,
    key
  };
}

function getPnrSectionContent() {
  const sectionTitle = ``;
  const paraContentCollection = [
    `Above all, you can also check your `,
    `<a href="https://www.makemytrip.com/railways/PNR/"><strong>PNR status</strong></a>`,
    ` by simply entering your 10-digit PNR number to check the details of your train tickets. In case you&rsquo;ve missed out on general tickets and need to book your seat in an emergency, you can also book a `,
    `<a href="https://www.makemytrip.com/railways/tatkal-railway-reservation.html"><strong>tatkal train ticket</strong></a>`,
    ` on MakeMyTrip.`
  ];
  const key = 'PnrSectionContent';
  return {
    sectionTitle,
    paraContentCollection,
    key
  };
}

export function getLtsRelatedInfo(trainRelatedInfo) {
  const sectionDetail = [
    getTrainAvailableClassContent,
    getTrainRouteContent,
    getTrainStatusInfoContent,
    getTrainScheduleInfoContent,
    getOnlineBookingInfoContent,
    getPnrSectionContent
  ].map((getSectionDetail) => getSectionDetail(trainRelatedInfo));
  return sectionDetail;
}
